import { Injectable, inject, signal } from '@angular/core';
import { GRAPHQL_API_URL } from './graphql.tokens';

export interface AuthUser {
  id: string;
  tenantId: string;
  email: string;
  displayName?: string | null;
  roles: string[];
}

const TENANT_ID = 'TENANT-SOFRONIOS-LEGACY';

export const GOOGLE_CLIENT_ID = '365326055756-rm7ikt060nfv7nnvlq3jptv01gmenvkm.apps.googleusercontent.com';

/** The redirect_uri to use when building the Google auth URL and when calling the backend. */
export function getGoogleRedirectUri(): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/oauth`;
}

/** Build the Google OAuth authorization URL. The browser is navigated to this URL. */
export function buildGoogleAuthUrl(): string {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: getGoogleRedirectUri(),
    response_type: 'code',
    scope: 'email profile',
    prompt: 'select_account',
  });
  return `https://accounts.google.com/o/oauth2/auth?${params.toString()}`;
}
const TOKEN_STORAGE_KEY = 'sofronios_auth_token';
const USER_STORAGE_KEY = 'sofronios_auth_user';

interface AuthPayload {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: AuthUser;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private url = inject(GRAPHQL_API_URL);

  readonly token = signal<string | null>(this.readStored(TOKEN_STORAGE_KEY));
  readonly currentUser = signal<AuthUser | null>(this.readStoredUser());

  /** Exchange a Google OAuth authorization code for an authenticated session. */
  async googleLogin(code: string, redirectUri: string): Promise<AuthUser> {
    const doc = `mutation LoginWithGoogle($input: GoogleLoginInput!) {
      loginWithGoogle(input: $input) {
        accessToken
        refreshToken
        user { id email displayName roles }
      }
    }`;
    const data = await this.gql<{ loginWithGoogle: { accessToken: string; refreshToken: string; user: { id: string; email: string; displayName: string | null; roles: string[] } } }>(
      doc,
      { input: { code, redirectUri } },
    );
    const u = data.loginWithGoogle.user;
    const authUser: AuthUser = { id: u.id, tenantId: 'TENANT-SOFRONIOS-LEGACY', email: u.email, displayName: u.displayName, roles: u.roles };
    this.persist(data.loginWithGoogle.accessToken, authUser);
    return authUser;
  }

  async login(email: string, password: string): Promise<AuthUser> {
    const doc = `mutation Login($input: LoginInput!) {
      login(input: $input) {
        accessToken
        refreshToken
        expiresIn
        user { id tenantId email displayName roles }
      }
    }`;
    const data = await this.gql<{ login: AuthPayload }>(doc, {
      input: { email, password, tenantId: TENANT_ID },
    });
    this.persist(data.login.accessToken, data.login.user);
    return data.login.user;
  }

  async logout(): Promise<void> {
    try {
      await this.gql<{ logout: boolean }>(`mutation { logout }`);
    } catch {
      /* best-effort */
    }
    this.clear();
  }

  /** Re-validate the stored token against the server (called on app boot). */
  async restore(): Promise<AuthUser | null> {
    if (!this.token()) return null;
    try {
      const data = await this.gql<{ me: AuthUser | null }>(
        `query { me { id tenantId email displayName roles } }`,
      );
      if (data.me) {
        this.currentUser.set(data.me);
        this.writeStored(USER_STORAGE_KEY, JSON.stringify(data.me));
        return data.me;
      }
      this.clear();
      return null;
    } catch {
      this.clear();
      return null;
    }
  }

  private async gql<T>(doc: string, variables?: Record<string, unknown>): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const t = this.token();
    if (t) headers['authorization'] = `Bearer ${t}`;
    const res = await fetch(this.url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query: doc, variables }),
    });
    const json = (await res.json()) as { data?: T; errors?: { message: string }[] };
    if (json.errors?.length) throw new Error(json.errors.map((e) => e.message).join('; '));
    if (!json.data) throw new Error('Empty GraphQL response');
    return json.data;
  }

  private persist(token: string, user: AuthUser): void {
    this.token.set(token);
    this.currentUser.set(user);
    this.writeStored(TOKEN_STORAGE_KEY, token);
    this.writeStored(USER_STORAGE_KEY, JSON.stringify(user));
  }

  private clear(): void {
    this.token.set(null);
    this.currentUser.set(null);
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      window.sessionStorage.removeItem(USER_STORAGE_KEY);
    }
  }

  private writeStored(key: string, value: string): void {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(key, value);
  }

  private readStored(key: string): string | null {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem(key);
  }

  private readStoredUser(): AuthUser | null {
    const raw = this.readStored(USER_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  }
}
